import { Box, IconButton, Tooltip } from "@mui/material";
import { ReactNode, useState } from "react";
import { FileCopy as CopyIcon, Check as CheckIcon } from "@mui/icons-material";
import { copyToClipboard } from "~/lib/clipboard";

export type CopyTextProps = {
  children: ReactNode;
  value?: string | null;
};

export function CopyText({ children, value }: CopyTextProps) {
  const textToCopy =
    value || (typeof children === "string" ? children : undefined);

  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  if (!textToCopy) return children;

  const onMouseEnter = () => setIsHovered(true);
  const onMouseLeave = () => setIsHovered(false);

  const onClick = async () => {
    await copyToClipboard(textToCopy);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 800);
  };

  return (
    <Box
      className={`${isHovered ? "hovered" : "not-hovered-not"}`}
      data-testid="copy-text"
      component="span"
      sx={{ fontSize: "inherit" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box component="span">{children}</Box>
      <Tooltip
        title={isCopied ? "Copied!" : "Copy to clipboard"}
        arrow
        placement="top"
      >
        <IconButton
          onClick={onClick}
          aria-label={isCopied ? "Copied to clipboard" : "Copy to clipboard"}
          sx={{
            fontSize: "inherit",
            pointerEvents: isHovered ? undefined : "none",
            opacity: isHovered ? 1 : 0,
            transition: (theme) => theme.transitions.create("opacity"),
            my: -1,
            ml: 0.5,
          }}
        >
          {isCopied ? (
            <CheckIcon fontSize="inherit" sx={{ color: "success.main" }} />
          ) : (
            <CopyIcon fontSize="inherit" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
